import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  CircularProgress,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  LocalHospital as Hospital,
  MedicalServices as Stethoscope,
  Science as FlaskConical,
  Business as Building2,
  LocalPharmacy as Pill,
  ChevronRight,
  Clear as ClearIcon,
  ShoppingCart,
  Search as SearchIcon,
  Apps as AppsIcon,
  People as PeopleIcon,
  Dashboard as DashboardIcon,
  TrendingUp,
} from '@mui/icons-material';

import { getAllDepartments, categorie } from '../data/department';
import { getAcc, accessibledepartments } from '../BaseURL';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import ConsumableRequisitionDialog from '../Procurement/ConsumableRequisitionDialog';

const Dashboard = () => {
  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState(null);
  const [getAccInfo, setgetAccInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [departments] = useState(getAllDepartments);
  const [categories] = useState(categorie);
  const [consumablesDialogOpen, setConsumablesDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // ── Load user info ─────────────────────────────────────────────────
  useEffect(() => {
    try {
      const savedUserInfo = localStorage.getItem('userInfo');
      if (savedUserInfo) {
        const parsed = JSON.parse(savedUserInfo);
        if (parsed && parsed.name && parsed.role) setUserInfo(parsed);
        else throw new Error('Invalid user info structure');
      } else {
        throw new Error('No user info found');
      }
    } catch (e) {
      console.error('Failed to load user info:', e);
      setError('Failed to load user information');
    }
  }, []);

  useEffect(() => { if (userInfo) loadRoles(); }, [userInfo]);

  const loadRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = userInfo.role === 'System Administrator' ? accessibledepartments : getAcc;
      const response = userInfo.role === 'System Administrator'
        ? await axios.get(endpoint)
        : await axios.post(endpoint, { role: userInfo.role });
      if (response.data.success && Array.isArray(response.data.results)) {
        setgetAccInfo(response.data.results);
      } else {
        throw new Error('Failed to load roles');
      }
    } catch (e) {
      const msg = e.response?.data?.message || 'Failed to load roles';
      setError(msg);
      await Swal.fire({ title: 'Error', text: msg, icon: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ── Access logic (role-based) ──────────────────────────────────────
  const accessibleDepartmentIds = useMemo(() => {
    if (!Array.isArray(getAccInfo)) return [];
    const ids = getAccInfo.map(item => item.department).filter(Boolean);
    return Array.from(new Set(ids));
  }, [getAccInfo]);

  const accessibleDepartments = useMemo(() => {
    if (!accessibleDepartmentIds.length) return [];
    return accessibleDepartmentIds
      .map(id => departments.find(d => d.id === id))
      .filter(Boolean);
  }, [accessibleDepartmentIds, departments]);

  const filteredDepartments = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return accessibleDepartments;
    return accessibleDepartments.filter(d =>
      d.name.toLowerCase().includes(q) ||
      (d.description || '').toLowerCase().includes(q)
    );
  }, [accessibleDepartments, searchQuery]);

  const groupedDepartments = useMemo(() => {
    const grouped = {};
    if (!filteredDepartments.length) return grouped;
    Object.entries(categories).forEach(([categoryName, deptIds]) => {
      const depts = filteredDepartments.filter(d => deptIds.includes(d.id));
      if (depts.length) grouped[categoryName] = depts;
    });
    const uncategorized = filteredDepartments.filter(d =>
      !Object.values(categories).flat().includes(d.id)
    );
    if (uncategorized.length) grouped['Other Services'] = uncategorized;
    return grouped;
  }, [filteredDepartments, categories]);

  const categoryCount = useMemo(() => Object.keys(groupedDepartments).length, [groupedDepartments]);

  // ── Navigation routing for departments (preserved) ─────────────────
  const onDepartmentSelect = (department) => {
    const map = {
      'Medical records':         '/dashboard/Record',
      'Pharmacy':                '/dashboard/Pharmacy',
      'Billing & Accounts':      '/dashboard/Accounts',
      'General Medical Services':'/dashboard/Doctors',
      'Wards & Nursing Services':'/dashboard/Wards',
      'Procurement & Supplies':  '/dashboard/Procurement',
      'Investigations':          '/dashboard/Laboratory',
      'Hospital Claims':         '/dashboard/Claims',
      'Health Information':      '/dashboard/HealthInformation',
      'Emergency (ER/A&E)':      '/dashboard/Emergency',
      'Human Resources (HR)':    '/dashboard/HumanResources',
    };
    const route = map[department.name];
    if (route) navigate(route);
  };

  const getCategoryIcon = (category) => {
    if (category.includes('Clinical')) return <Stethoscope />;
    if (category.includes('Diagnostic')) return <FlaskConical />;
    if (category.includes('Administrative')) return <Building2 />;
    if (category.includes('Specialty')) return <Pill />;
    return <Stethoscope />;
  };

  // Per-category accent color (used for the category header pill, the card
  // top stripe, the card icon background, and the hover glow).
  const accentByCategory = (category) => {
    if (category.includes('Clinical'))       return '#22C55E'; // green
    if (category.includes('Diagnostic'))     return '#3B82F6'; // blue
    if (category.includes('Administrative')) return '#F59E0B'; // amber
    if (category.includes('Specialty'))      return '#A855F7'; // purple
    return '#6366F1';                                           // indigo
  };

  // ── Loading & error early returns ──────────────────────────────────
  if (loading || !userInfo) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={48} thickness={4} sx={{ color: 'primary.main' }} />
          <Typography sx={{ fontWeight: 600 }} color="text.primary">Loading Dashboard…</Typography>
          <Typography variant="body2" color="text.secondary">Preparing your workspace</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <Paper sx={{ p: 5, textAlign: 'center', maxWidth: 460, borderRadius: 3 }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'error.main', mx: 'auto', mb: 2, display: 'grid', placeItems: 'center' }}>
            <ClearIcon sx={{ color: '#fff', fontSize: 32 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Something went wrong</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>{error}</Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>Reload</Button>
        </Paper>
      </Box>
    );
  }

  const firstName = (userInfo?.name || '').trim().split(/\s+/)[0] || 'there';
  const todayStr = new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  // ── Main render ────────────────────────────────────────────────────
  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: 'background.default',
      color: 'text.primary',
      py: { xs: 2, md: 3 },
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* ── Animated background blobs + drifting sparks ──────── */}
      <style>{`
        @keyframes dashBlobA { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(40px,-30px) scale(1.05); } 66% { transform: translate(-30px,30px) scale(0.95); } }
        @keyframes dashBlobB { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-50px,-40px) scale(1.08); } }
        @keyframes dashBlobC { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(35px,40px) scale(0.92); } }
        @keyframes dashBlobD { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-20px,30px) scale(1.04); } }
        @keyframes dashSparkRise {
          0%   { transform: translate(0, 0) scale(0.6);  opacity: 0; }
          10%  { opacity: 0.65; }
          50%  { transform: translate(20px, -45vh) scale(1); opacity: 1; }
          90%  { opacity: 0.35; }
          100% { transform: translate(-12px, -95vh) scale(0.5); opacity: 0; }
        }
        .dash-spark {
          position: absolute;
          bottom: -12px;
          border-radius: 50%;
          pointer-events: none;
          animation: dashSparkRise linear infinite;
        }
      `}</style>
      <Box aria-hidden sx={{
        position: 'absolute', inset: 0, zIndex: 0,
        pointerEvents: 'none', overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute',
          width: 520, height: 520, top: -200, left: -160,
          borderRadius: '50%', filter: 'blur(80px)',
          background: 'radial-gradient(circle, rgba(34,197,94,0.18) 0%, transparent 70%)',
          animation: 'dashBlobA 22s ease-in-out infinite',
        }} />
        <Box sx={{
          position: 'absolute',
          width: 440, height: 440, top: '22%', right: -180,
          borderRadius: '50%', filter: 'blur(80px)',
          background: 'radial-gradient(circle, rgba(168,85,247,0.18) 0%, transparent 70%)',
          animation: 'dashBlobB 28s ease-in-out infinite',
        }} />
        <Box sx={{
          position: 'absolute',
          width: 400, height: 400, bottom: -160, left: '30%',
          borderRadius: '50%', filter: 'blur(80px)',
          background: 'radial-gradient(circle, rgba(59,130,246,0.18) 0%, transparent 70%)',
          animation: 'dashBlobC 26s ease-in-out infinite',
        }} />
        <Box sx={{
          position: 'absolute',
          width: 360, height: 360, top: '55%', left: -120,
          borderRadius: '50%', filter: 'blur(80px)',
          background: 'radial-gradient(circle, rgba(245,158,11,0.14) 0%, transparent 70%)',
          animation: 'dashBlobD 30s ease-in-out infinite',
        }} />

        {/* Drifting sparks (the "flies" in the background) */}
        {[...Array(18)].map((_, i) => {
          const palette = ['#22C55E', '#3B82F6', '#A855F7', '#F59E0B', '#6366F1'];
          const color = palette[i % palette.length];
          const left = (i * 6.7 + 4) % 100;
          const size = 3 + (i % 3);
          const dur = 14 + (i % 5) * 3;
          const delay = (i * 1.3) % 14;
          return (
            <span
              key={i}
              className="dash-spark"
              style={{
                left: `${left}%`,
                width: size,
                height: size,
                background: color,
                boxShadow: `0 0 8px ${color}, 0 0 14px ${color}88`,
                animationDuration: `${dur}s`,
                animationDelay: `-${delay}s`,
              }}
            />
          );
        })}
      </Box>

      <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 4 }, position: 'relative', zIndex: 1 }}>

      {/* ── Welcome card ─────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4.5 },
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Box sx={{
            width: 9, height: 9, borderRadius: '50%',
            bgcolor: '#22C55E', boxShadow: '0 0 12px rgba(34,197,94,0.7)',
          }} />
          <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.5, color: '#22C55E' }}>
            DASHBOARD · LIVE
          </Typography>
        </Stack>

        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: 30, md: 42 },
            letterSpacing: '-0.8px',
            lineHeight: 1.1,
            mb: 1.25,
          }}
        >
          Welcome back, {firstName}
        </Typography>

        <Typography color="text.secondary" sx={{ fontSize: 15.5, mb: userInfo?.is_staff === 1 ? 3 : 0 }}>
          {todayStr} · Select a department below to begin your work.
        </Typography>

        {userInfo?.is_staff === 1 && (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
          >
            <Button
              variant="contained"
              startIcon={<ShoppingCart />}
              onClick={() => setConsumablesDialogOpen(true)}
              sx={{
                bgcolor: '#22C55E',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                px: 2.5,
                py: 1.1,
                boxShadow: '0 6px 18px rgba(34,197,94,0.32)',
                '&:hover': { bgcolor: '#16A34A' },
              }}
            >
              Request Consumables
            </Button>
            <Typography variant="body2" color="text.secondary">
              In-charge access — send a request to Procurement for cotton, plaster, cannulas, etc.
            </Typography>
          </Stack>
        )}
      </Paper>

      {/* ── Stat tiles ───────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'DEPARTMENTS', value: accessibleDepartments.length, icon: <AppsIcon />,        color: '#22C55E' },
          { label: 'YOUR ROLE',   value: userInfo?.role || '—',         icon: <PeopleIcon />,      color: '#6366F1' },
          { label: 'CATEGORIES',  value: categoryCount,                  icon: <DashboardIcon />,   color: '#A855F7' },
          { label: 'STATUS',      value: 'Online',                       icon: <TrendingUp />,      color: '#22C55E' },
        ].map(s => (
          <Grid item xs={6} sm={6} md={3} key={s.label}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1.75,
                minHeight: 78,
              }}
            >
              <Box sx={{
                width: 44, height: 44, borderRadius: 1.75,
                bgcolor: `${s.color}1F`, color: s.color,
                display: 'grid', placeItems: 'center',
                flexShrink: 0,
              }}>
                {React.cloneElement(s.icon, { sx: { fontSize: 22 } })}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{
                  fontSize: 11, fontWeight: 800, letterSpacing: '.7px',
                  color: 'text.secondary',
                }}>
                  {s.label}
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.2 }} noWrap title={String(s.value)}>
                  {s.value}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ── Search ───────────────────────────────────────────────── */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search departments…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            sx: { borderRadius: 2.5, bgcolor: 'background.paper' },
          }}
        />
        {searchQuery && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Found <strong>{filteredDepartments.length}</strong> department{filteredDepartments.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>

      {/* ── Department grid (role-based) ─────────────────────────── */}
      {Object.keys(groupedDepartments).length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
          <Hospital sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography sx={{ fontWeight: 600 }}>No accessible departments</Typography>
          <Typography variant="body2" color="text.secondary">Please contact your administrator for access.</Typography>
        </Paper>
      ) : (
        Object.entries(groupedDepartments).map(([category, depts]) => {
          const accent = accentByCategory(category);
          return (
            <Box key={category} sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: 1.75,
                  bgcolor: `${accent}1F`, color: accent,
                  display: 'grid', placeItems: 'center',
                }}>
                  {React.cloneElement(getCategoryIcon(category), { sx: { fontSize: 20 } })}
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 17, lineHeight: 1.2 }}>{category}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {depts.length} {depts.length === 1 ? 'department' : 'departments'}
                  </Typography>
                </Box>
              </Stack>
              <Grid container spacing={2}>
                {depts.map(d => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={d.id}>
                    <Paper
                      onClick={() => onDepartmentSelect(d)}
                      elevation={0}
                      sx={{
                        p: 0, borderRadius: 2, cursor: 'pointer',
                        border: '1px solid', borderColor: 'divider',
                        overflow: 'hidden',
                        transition: 'transform .15s ease, border-color .15s ease, box-shadow .15s ease',
                        '&:hover': { transform: 'translateY(-3px)', borderColor: accent, boxShadow: `0 10px 24px ${accent}26` },
                      }}
                    >
                      <Box sx={{ height: 3, bgcolor: accent }} />
                      <Box sx={{ p: 2 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
                          <Box sx={{
                            width: 34, height: 34, borderRadius: 1.5,
                            bgcolor: `${accent}1F`, color: accent,
                            display: 'grid', placeItems: 'center',
                          }}>
                            {React.cloneElement(getCategoryIcon(category), { sx: { fontSize: 18 } })}
                          </Box>
                          <Box sx={{
                            width: 28, height: 28, borderRadius: 1.25,
                            bgcolor: 'action.hover', color: 'text.secondary',
                            display: 'grid', placeItems: 'center',
                          }}>
                            <ChevronRight fontSize="small" />
                          </Box>
                        </Stack>
                        <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 0.5 }} noWrap title={d.name}>
                          {d.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.5,
                        }}>
                          {d.description}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        })
      )}

      <ConsumableRequisitionDialog
        open={consumablesDialogOpen}
        onClose={() => setConsumablesDialogOpen(false)}
      />
      </Box>
    </Box>
  );
};

export default Dashboard;
